import { useMemo } from 'react'
import styled from 'styled-components'
import { match, P } from 'ts-pattern'

import { Tooltip } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import { DevBurnIcon } from '@root/shared/ui-kit'

import type { TradeType } from '../../model'
import type { ClassNameProps } from '@root/shared/lib/react'
import type { CSSProperties } from 'styled-components'

type Props = {
  type: TradeType.ExtendedTxType
  size?: number
} & ClassNameProps

export const SwapDirectionCell: React.FC<Props> = ({
  type,
  className,
  size = 24,
}) => {
  const { t } = useTranslation()

  const tooltip = match(type)
    .with(P.union('buy', 'market-buy', 'limit-buy'), () =>
      t('trade.type.tooltip.buy'),
    )
    .with(P.union('sell', 'market-sell', 'limit-sell'), () =>
      t('trade.type.tooltip.sell'),
    )
    .with('poolInit', () => t('trade.type.tooltip.poolInit'))
    .with('liquidityAdd', () => t('trade.type.tooltip.liquidityAdd'))
    .with('liquidityRemove', () => t('trade.type.tooltip.liquidityRemove'))
    .with('poolMigrate', () => t('trade.type.tooltip.poolMigrate'))
    .with('quoteBurn', () => t('trade.type.tooltip.burn'))
    .with('lpBurn', () => t('trade.type.tooltip.lpBurn'))
    .exhaustive()

  const label = match(type)
    .with(P.union('buy', 'market-buy', 'limit-buy'), () => 'B')
    .with(P.union('sell', 'market-sell', 'limit-sell'), () => 'S')
    .with('poolInit', () => 'PI')
    .with('liquidityAdd', () => 'LA')
    .with('liquidityRemove', () => 'LR')
    .with('poolMigrate', () => 'PM')
    .with('quoteBurn', () => <DevBurnIcon size={14} />)
    .with('lpBurn', () => 'LB')
    .exhaustive()

  const style = useMemo(() => {
    return match(type)
      .returnType<CSSProperties>()
      .with(P.union('buy', 'market-buy', 'limit-buy'), () => ({
        color: 'var(--up-9)',
        background: 'var(--upA-3)',
        border: 'none',
      }))
      .with(P.union('sell', 'market-sell', 'limit-sell'), () => ({
        color: 'var(--down-9)',
        background: 'var(--downA-3)',
        border: 'none',
      }))
      .with('poolInit', () => ({
        color: 'var(--background-9)',
        background: 'var(--background-3)',
        border: 'none',
      }))
      .with('liquidityAdd', () => ({
        color: 'var(--up-9)',
        background: 'transparent',
        border: '1px solid rgba(var(--up-9-rgb), 0.25)',
      }))
      .with('liquidityRemove', () => ({
        color: 'var(--down-9)',
        background: 'transparent',
        border: '1px solid rgba(var(--down-9-rgb), 0.30)',
      }))
      .with('poolMigrate', () => ({
        color: 'var(--warning-9)',
        background: 'transparent',
        border: 'none',
      }))
      .with('quoteBurn', () => ({
        color: 'var(--amber-9)',
        background: 'var(--amberA-3)',
        border: 'none',
      }))
      .with('lpBurn', () => ({
        color: 'var(--background-9)',
        background: 'var(--background-3)',
        border: 'none',
      }))
      .exhaustive()
  }, [type])

  return (
    <Tooltip content={tooltip}>
      <Root style={style} $size={size} className={className}>
        {label}
      </Root>
    </Tooltip>
  )
}

const Root = styled.div<{ $size: number }>`
  display: flex;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  padding-block: 4px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;

  border-radius: 8px;
`
